import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

import { Container } from "./Container";
import { Icon } from "./Icon";
import { useTamanhoTela } from "../hooks/useTamanhoTela";
import { waLink } from "../data/diagnostico";

import LOGO from "../assets/media/logo_nav.png";

const UtilityBar = styled.div`
    width: 100%;
    background: var(--ink);
    color: rgba(255, 255, 255, 0.82);
    font-size: 12.5px;
    font-weight: 600;

    @media only screen and (max-width: 768px) {
        display: none;
    }
`;

const UtilityRow = styled.div`
    width: min(1180px, calc(100vw - 24px));
    margin: 0 auto;
    min-height: 34px;
    display: flex;
    align-items: center;
    gap: 22px;
`;

const UtilityItem = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${({ $bright }) => ($bright ? "#ffffff" : "rgba(255, 255, 255, 0.82)")};
    font-weight: 700;

    .material-symbols-rounded { color: var(--brand-3); font-size: 15px; }
`;

const UtilityContacts = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 20px;
`;

const UtilityLink = styled.a`
    color: #ffffff;
    font-weight: 700;

    &:hover { color: var(--brand-3); }
`;

const StyledNav = styled.nav`
    width: 100%;
    padding: ${(props) => (props.$scrolled ? "12px 0" : "0")};
    z-index: 20;
    display: flex;
    justify-content: center;
    position: ${(props) => (props.$scrolled ? "fixed" : "absolute")};
    left: 0;
    top: ${(props) => (props.$scrolled ? "0" : "34px")};
    pointer-events: none;
    transition: padding 0.28s ease, top 0.28s ease;

    @media only screen and (max-width: 768px) {
        position: fixed;
        top: 0;
        padding: 10px 0 0;
    }
`;

const NavPanel = styled.div`
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: ${(props) => (props.$scrolled ? "12px 32px" : "16px 32px")};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: ${(props) => (props.$scrolled ? "10px" : "18px")};
    border-radius: 0;
    background: ${(props) => (props.$scrolled ? "rgba(255, 255, 255, 0.86)" : "transparent")};
    border-bottom: 1px solid ${(props) => (props.$scrolled ? "var(--line)" : "transparent")};
    box-shadow: ${(props) => (props.$scrolled ? "0 20px 55px rgba(26, 19, 48, 0.14)" : "none")};
    backdrop-filter: ${(props) => (props.$scrolled ? "blur(16px)" : "none")};
    pointer-events: auto;
    transition: padding 0.28s ease, background-color 0.28s ease,
        border-color 0.28s ease, box-shadow 0.28s ease, backdrop-filter 0.28s ease;

    @media only screen and (max-width: 768px) {
        width: 100%;
        padding: 10px 16px;
        border-radius: 0;
        background: rgba(255, 255, 255, 0.92);
        border-bottom: 1px solid var(--line);
        box-shadow: 0 14px 38px rgba(26, 19, 48, 0.14);
        backdrop-filter: blur(16px);
    }
`;

const LogoContainer = styled.a`
    all: unset;
    cursor: pointer;
`;

const LogoIcon = styled.img`
    width: ${(props) => (props.$scrolled ? "84px" : "94px")};
    height: auto;
    transition: width 0.28s ease;

    @media only screen and (max-width: 768px) {
        width: 78px;
    }
`;

const DesktopLinks = styled.div`
    display: flex;
    align-items: center;
    margin-left: ${(props) => (props.$scrolled ? "22px" : "34px")};
    gap: ${(props) => (props.$scrolled ? "18px" : "26px")};
    transition: margin-left 0.28s ease, gap 0.28s ease;
`;

const NavLink = styled.a`
    color: ${(props) => (props.$active ? "var(--brand)" : "var(--ink)")};
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    border-bottom: 2px solid ${(props) => (props.$active ? "var(--brand)" : "transparent")};
    padding-bottom: 2px;
    transition: color 0.2s ease, transform 0.2s ease;

    &:hover {
        color: var(--brand);
        transform: translateY(-1px);
    }
`;

const NavActions = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 10px;
`;

const GhostCta = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 12px;
    border: 1.5px solid #d9d0ee;
    color: var(--ink);
    font-weight: 700;
    transition: border-color 0.2s ease, color 0.2s ease;

    &:hover {
        border-color: var(--brand);
        color: var(--brand-2);
    }

    @media only screen and (max-width: 900px) {
        display: none;
    }
`;

const NavCta = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 18px;
    border-radius: 12px;
    background: var(--ink);
    color: #ffffff;
    font-weight: 800;
    box-shadow: ${(props) => (props.$scrolled ? "0 14px 28px rgba(26, 19, 48, 0.18)" : "none")};
    transition: transform 0.25s ease, box-shadow 0.25s ease, background-color 0.2s ease;

    &:hover {
        background: #2a1f4d;
        transform: translateY(-2px);
    }

    @media only screen and (max-width: 768px) {
        display: none;
    }
`;

const MenuButton = styled.button`
    display: none;
    width: 44px;
    height: 44px;
    border: 1px solid var(--line);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.7);
    color: var(--ink);
    cursor: pointer;
    padding: 0;
    align-items: center;
    justify-content: center;
    transition: background-color 0.25s ease, border-color 0.25s ease;

    @media only screen and (max-width: 768px) {
        display: inline-flex;
        width: 42px;
        height: 42px;
        border-radius: 14px;
        background: var(--bg-soft);
    }
`;

const MobileSheet = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 10px;
    left: 10px;
    width: auto;
    padding: 10px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid var(--line);
    box-shadow: var(--shadow-strong);
    backdrop-filter: blur(16px);
    pointer-events: auto;
    display: ${(props) => (props.$open ? "flex" : "none")};
    flex-direction: column;
    gap: 8px;

    @media only screen and (min-width: 769px) {
        display: none;
    }
`;

const MobileLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    padding: 0 14px;
    border-radius: 12px;
    color: var(--ink);
    background: var(--bg-soft);
    font-weight: 700;

    .material-symbols-rounded { color: var(--muted-soft); }
`;

const MobileCta = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 50px;
    border-radius: 12px;
    color: #ffffff;
    background: var(--brand);
    font-weight: 800;
`;

const NAV_LINKS = [
    { label: "Diagnóstico", homeHref: "/#diagnostico", href: "/#diagnostico" },
    { label: "Serviços", homeHref: "/#servicos", href: "/#servicos" },
    { label: "Cases", homeHref: "/cases", href: "/cases" },
    { label: "Blog", homeHref: "/blog", href: "/blog" }
];

function Nav() {
    const { isMobile } = useTamanhoTela();
    const { pathname } = useLocation();
    const [isDetached, setIsDetached] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => setIsDetached(window.scrollY > 10);
        handleScroll();
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        if (!isMobile) setMenuOpen(false);
    }, [isMobile]);

    useEffect(() => {
        setMenuOpen(false);
    }, [pathname]);

    const waGeral = waLink("Olá! Vim pelo site da Mango e quero conversar sobre o meu caso.");

    return (
        <>
            <UtilityBar>
                <UtilityRow>
                    <UtilityItem $bright>
                        <Icon name="public" size="15px" />
                        Atendemos empresas em todo o Brasil e em Portugal
                    </UtilityItem>
                    <UtilityItem>
                        <Icon name="badge" size="15px" />
                        CNPJ 33.545.792/0001-39
                    </UtilityItem>
                    <UtilityContacts>
                        <UtilityLink href="tel:+554191150056">+55 41 9115-0056</UtilityLink>
                        <UtilityLink href="mailto:contato@mangosoftwarehouse.com">contato@mangosoftwarehouse.com</UtilityLink>
                    </UtilityContacts>
                </UtilityRow>
            </UtilityBar>

            <StyledNav $scrolled={isDetached}>
                <Container css="width: 100%; position: relative;">
                    <NavPanel $scrolled={isDetached}>
                        <LogoContainer href="/" aria-label="Mango Software House — início">
                            <LogoIcon src={LOGO} alt="Mango Software House" $scrolled={isDetached} />
                        </LogoContainer>

                        {!isMobile && (
                            <DesktopLinks $scrolled={isDetached}>
                                {NAV_LINKS.map((link) => (
                                    <NavLink key={link.label} href={pathname === "/" ? link.homeHref : link.href} $active={pathname === link.href}>
                                        {link.label}
                                    </NavLink>
                                ))}
                            </DesktopLinks>
                        )}

                        <NavActions>
                            <GhostCta href={pathname === "/" ? "/#form" : "/#diagnostico"}>Pedir diagnóstico</GhostCta>
                            <NavCta href={waGeral} target="_blank" rel="noreferrer" $scrolled={isDetached}>
                                WhatsApp
                                <Icon name="arrow_outward" size="17px" />
                            </NavCta>

                            <MenuButton
                                type="button"
                                aria-label={menuOpen ? "Fechar navegação" : "Abrir navegação"}
                                aria-expanded={menuOpen}
                                onClick={() => setMenuOpen((prev) => !prev)}
                            >
                                <Icon name={menuOpen ? "close" : "menu"} size="22px" />
                            </MenuButton>
                        </NavActions>
                    </NavPanel>

                    <MobileSheet $open={menuOpen}>
                        {NAV_LINKS.map((link) => (
                            <MobileLink key={link.label} href={pathname === "/" ? link.homeHref : link.href} onClick={() => setMenuOpen(false)}>
                                {link.label}
                                <Icon name="chevron_right" size="18px" />
                            </MobileLink>
                        ))}
                        <MobileLink href="/privacidade" onClick={() => setMenuOpen(false)}>
                            Privacidade
                            <Icon name="chevron_right" size="18px" />
                        </MobileLink>
                        <MobileCta href={waGeral} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>
                            <Icon name="chat" size="19px" />
                            Falar no WhatsApp
                        </MobileCta>
                    </MobileSheet>
                </Container>
            </StyledNav>
        </>
    );
}

export { Nav };
export default Nav;
